<template>
  <div class="internal-bady" :dir="pageDirection" :lang="pageLanguage">
    <WhatsappChet />
    <NavBar />
    <div id="top"></div>

    <section class="start-insert-section px-3 px-md-4">
      <div class="container start-insert-container">
        <div
          v-if="isMobile"
          class="start-insert-mobile-hint mb-4"
        >
          <div class="start-insert-mobile-hint-top" role="status">
            <span class="start-insert-mobile-hint-icon" aria-hidden="true">
              <i class="fa-solid fa-desktop"></i>
            </span>
            <span class="start-insert-mobile-hint-text">
              {{ t("startInsertRideMobileHint") }}
            </span>
          </div>
          <div class="start-insert-mobile-hint-actions">
            <a
              :href="whatsappShareWelcomeHref"
              class="btn start-insert-whatsapp-btn start-insert-mobile-hint-whatsapp fw-semibold"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fa-brands fa-whatsapp start-insert-whatsapp-btn-icon" aria-hidden="true"></i>
              {{ t("startInsertRideWhatsappShareButton") }}
            </a>
            <button
              type="button"
              class="start-insert-copy-welcome-link"
              @click="copyWelcomeRideManagersLink"
            >
              {{ t("startInsertRideCopyWelcomeLinkLine") }}
            </button>
            <p
              v-if="welcomeLinkJustCopied"
              class="start-insert-copy-welcome-feedback mb-0"
              role="status"
            >
              {{ t("linkCopiedSuccess") }}
            </p>
          </div>
        </div>
        <h1 class="start-insert-title">{{ t("startInsertRideTitle") }}</h1>
        <p class="start-insert-subtitle">{{ t("startInsertRideSubtitle") }}</p>

        <CreateRideChoices @choose="goToCreateRide" />
      </div>
    </section>

    <PageFooterNew />
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import PageFooterNew from "@/components/PageFooterNew.vue";
import WhatsappChet from "@/components/WhatsappChet";
import CreateRideChoices from "@/components/CreateRideChoices.vue";
import { translationMixin } from "@/mixins/translationMixin";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "StartInsertRide",
  mixins: [translationMixin],
  components: {
    NavBar,
    PageFooterNew,
    WhatsappChet,
    CreateRideChoices,
  },
  data() {
    return {
      welcomeLinkJustCopied: false,
      welcomeLinkCopyTimer: null,
    };
  },
  computed: {
    pageLanguage() {
      return this.getCurrentLanguage === "he" ? "he" : "en";
    },
    pageDirection() {
      return this.getCurrentLanguage === "he" ? "rtl" : "ltr";
    },
    ...mapGetters([
      "isAppLoggedIn",
      "isUserManager",
      "isMobile",
    ]),
    welcomeRideManagersAbsoluteUrl() {
      const base = this.$store.state.regionConfig.baseUrl || (typeof window !== "undefined" ? window.location.origin : 'https://tripplanner.co.il');
      return `${base}/welcome-ride-managers`;
    },
    whatsappShareWelcomeHref() {
      const link = this.welcomeRideManagersAbsoluteUrl;
      const text = `${this.t("startInsertRideWhatsappShareMessageLine1")}\n${this.t(
        "startInsertRideWhatsappShareMessageLine2"
      )} ${link}`;
      return `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    },
  },
  beforeDestroy() {
    if (this.welcomeLinkCopyTimer) {
      clearTimeout(this.welcomeLinkCopyTimer);
      this.welcomeLinkCopyTimer = null;
    }
  },
  methods: {
    ...mapActions([
      "showModal",
      "showModalWithParameters",
      "openLoginForm",
      "makeCurrentUserRideManager",
    ]),
    copyWelcomeRideManagersLink() {
      const url = this.welcomeRideManagersAbsoluteUrl;
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.welcomeLinkJustCopied = true;
          if (this.welcomeLinkCopyTimer) {
            clearTimeout(this.welcomeLinkCopyTimer);
          }
          this.welcomeLinkCopyTimer = setTimeout(() => {
            this.welcomeLinkJustCopied = false;
            this.welcomeLinkCopyTimer = null;
          }, 2500);
        })
        .catch(() => {});
    },
    async goToCreateRide(path) {
      // Require login before starting onboarding - show explanatory message first
      if (!this.isAppLoggedIn) {
        this.$store.dispatch("showModalWithParameters", {
          errorNumber: "startInsertRideLoginRequired",
          type: "textConverter",
        });
        return;
      }

      if (!this.isUserManager) {
        this.showModal("loadingIcon");
        await this.makeCurrentUserRideManager();
        this.showModal("");
        if (!this.isUserManager) {
          return;
        }
      }

      this.$router.push({ path, query: { from_start: "1" } });
    },
  },
};
</script>

<style scoped>
.internal-bady {
  line-height: 1.5;
  /* Fill the screen so the footer sits at the bottom even on a tall window. */
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.start-insert-section {
  flex: 1;
  display: flex;
  align-items: center;
  padding-top: 120px;
  padding-bottom: 5rem;
  background:
    radial-gradient(circle at 12% 20%, rgba(52, 208, 182, 0.16), transparent 42%),
    radial-gradient(circle at 88% 80%, rgba(7, 160, 195, 0.14), transparent 45%),
    linear-gradient(180deg, #f6fcfd 0%, #ffffff 100%);
}

.start-insert-container {
  max-width: 960px;
}

.start-insert-mobile-hint {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  background: linear-gradient(
    135deg,
    rgba(52, 208, 182, 0.35),
    rgba(7, 160, 195, 0.28)
  );
  border: 2px solid rgba(7, 160, 195, 0.55);
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  line-height: 1.4;
  box-shadow:
    0 6px 22px rgba(7, 160, 195, 0.28),
    0 2px 0 rgba(255, 255, 255, 0.6) inset;
  border-inline-start: 5px solid #07a0c3;
}

.start-insert-mobile-hint-top {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  font-size: 1.55rem;
  font-weight: 700;
  text-align: start;
  color: #063542;
}

.start-insert-mobile-hint-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  min-height: 2.5rem;
  display: grid;
  place-items: center;
  line-height: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #34d0b6, #07a0c3);
  color: #fff;
  box-shadow: 0 3px 12px rgba(7, 160, 195, 0.45);
}

.start-insert-mobile-hint-icon i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25em;
  height: 1.25em;
  margin: 0;
  line-height: 1;
  font-size: 1.15rem;
}

.start-insert-mobile-hint-text {
  flex: 1;
  padding-top: 0.1rem;
}

.start-insert-title {
  font-size: calc(2.8rem + 1.5vw);
  font-weight: 800;
  text-align: center;
  background: linear-gradient(135deg, #34D0B6, #07A0C3);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent !important;
  margin-bottom: 1rem;
}

.start-insert-subtitle {
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  color: #4a5a60;
  margin-bottom: 3.5rem;
}


.start-insert-mobile-hint-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
}

.start-insert-mobile-hint-whatsapp {
  width: 100%;
  display: inline-flex !important;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.start-insert-whatsapp-btn-icon {
  flex-shrink: 0;
}

.start-insert-copy-welcome-link {
  width: 100%;
  padding: 0.35rem 0.5rem;
  border: 0;
  background: transparent;
  color: #0a6f86;
  font-size: 1.2rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.start-insert-copy-welcome-link:hover {
  color: #063542;
}

.start-insert-copy-welcome-feedback {
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  color: #0d5c6e;
}

.start-insert-whatsapp-btn {
  padding: 14px 36px;
  font-size: 1.25rem !important;
  border-radius: 14px !important;
  background: #25d366;
  color: #fff !important;
  border: 0;
}

.start-insert-whatsapp-btn:hover {
  background: #20bd5a;
  color: #fff !important;
}

@media (max-width: 575.98px) {
  .start-insert-section {
    padding-top: 96px;
  }
}
</style>
